import ModalHeader from '@molecules/ModalHeader';
import ModalWindow from '@organisms/modals/ModalWindow';
import {FC, useState} from 'react';
import {
  ButtonsWrapper,
  ModalContent,
  ModalWrapper,
  OptionsGroup,
  HeaderInfo,
  DateRow,
  SectionCard,
  SecondarySectionCard,
  SectionWrapper,
  CopyLinkWrapper,
  NoticeRow,
  NoticeIconWrapper,
} from './ReportExportModal.styles';
import {Export, Link, WarningFilled} from '@carbon/icons-react';
import {theme} from '@styles/theme';
import RadioButton from '@atoms/RadioButton';
import Checkbox from '@atoms/Checkbox';
import Button from '@atoms/Button';
import Text from '@atoms/Text';
import {CalendarAddAlt} from '@carbon/icons-react';
import {formatTodayFull, formatNonTodayFull} from '@utils/common';
import {Formik} from 'formik';
import {ISubsystem, ISystemExportData} from '@types';
import Toast from '@atoms/Toast';
import {toast} from 'react-toastify';
import {systemsAPI} from '@api/methods';

interface Props {
  isOpened: boolean;
  onCloseRequest: () => void;
  systemID?: number;
  systemName?: string;
  onSubmit?: (values: FormValues) => void;
  subsystems?: ISubsystem[];
  leftDate?: Date;
  rightDate?: Date;
  afterSnapshotID?: number;
  beforeSnapshotID?: number;
}

export interface FormValues {
  format: 'pdf' | 'csv';
  includeAllSubsystems: boolean;
  detailsLevel: 'full' | 'summaryAll' | 'summaryOnly';
  selectedSubsystemIds: number[];
}

const ReportExportModal: FC<Props> = ({
  isOpened,
  onCloseRequest,
  systemName,
  systemID,
  onSubmit,
  subsystems,
  leftDate,
  rightDate,
  afterSnapshotID,
  beforeSnapshotID,
}) => {
  const [exportLoading, setExportLoading] = useState(false);
  const initialValues: FormValues = {
    format: 'pdf',
    includeAllSubsystems: true,
    detailsLevel: 'full',
    selectedSubsystemIds: [],
  };
  const today = new Date();

  const handleSubmit = async (values: FormValues) => {
    console.log('Report export form values:', values);
    setExportLoading(true);
    if (systemID) {
      const exportData: ISystemExportData = {
        in_export: values.includeAllSubsystems ? 1 : 2, // ALL_SUBSYSTEMS: 1, SELECTED_SUBSYSTEMS: 2
        subsystems_ids: values.selectedSubsystemIds,
        // export_type: EXPORT_ALL = 1, EXPORT_SUBSYSTEMS_MODULES = 2, EXPORT_SUBSYSTEMS_ONLY = 3
        export_type:
          values.detailsLevel === 'full'
            ? 1
            : values.detailsLevel === 'summaryAll'
              ? 2
              : 3,
        response_type: values.format === 'pdf' ? 1 : 2, // PDF = 1 URL = 2
      };
      if (afterSnapshotID) {
        exportData.after_snapshot_id = afterSnapshotID;
      }
      if (beforeSnapshotID) {
        exportData.before_snapshot_id = beforeSnapshotID;
      }
      const res = await systemsAPI.exportSystem({system_id: systemID, exportData});

      let newWindow;
      let url: string;
      if (values.format === 'pdf') {
        const blob = new Blob([res], {type: 'application/pdf'});
        url = window.URL.createObjectURL(blob);
        newWindow = window.open(url, '_blank');
      } else {
        url = res;
        newWindow = url;
        navigator.clipboard
          .writeText(url)
          .then(() => {
            toast.success(
              <Toast title="Ссылка скопирована в буфер обмена" type="success" />,
            );
            newWindow = true;
          })
          .catch(err => {
            console.error('Ошибка при копировании ссылки: ', err);
            toast.error(<Toast title="Не удалось скопировать ссылку" type="error" />);
          });
      }
      // Если браузер блокирует popup, открываем через ссылку в body
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = `system_${systemID}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Очищаем URL когда он больше не нужен
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    }
    setExportLoading(false);
    if (onSubmit) onSubmit(values);
    onCloseRequest();
  };

  return (
    <ModalWindow isOpen={isOpened}>
      <ModalWrapper>
        <ModalHeader
          title={'Сформировать отчёт'}
          onClose={onCloseRequest}
          iconElement={<Export color={theme.colors.basic03} size={24} />}
        />
        <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
          {({values, setFieldValue, handleSubmit}) => (
            <form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit(e as any);
              }}>
              <ModalContent>
                <HeaderInfo>
                  <Text
                    text={
                      systemName ?? 'Название Системы Long-Smart Placeholder 50 Char MAX'
                    }
                    type="captionB"
                  />
                  <DateRow>
                    <CalendarAddAlt size={16} color={theme.colors.basic02} />
                    <Text
                      text={
                        leftDate && rightDate
                          ? `${formatNonTodayFull(
                              leftDate < rightDate ? leftDate : rightDate,
                            )} / ${formatNonTodayFull(
                              leftDate > rightDate ? leftDate : rightDate,
                            )}`
                          : formatTodayFull(today)
                      }
                      type="bodyM"
                      color={theme.colors.basic02}
                    />
                  </DateRow>
                  {leftDate && rightDate && (
                    <NoticeRow>
                      <NoticeIconWrapper>
                        <WarningFilled color={theme.colors.basic04} size={21} />
                      </NoticeIconWrapper>
                      <div>
                        <Text text={'Обратите внимание'} type="bodyM" />
                        <Text
                          text={
                            'Будет сгенерирован отчёт, в котором будут сравниваться данные о покрытии за указанные даты.'
                          }
                          type="tinyR"
                          color={theme.colors.basic02}
                        />
                      </div>
                    </NoticeRow>
                  )}
                </HeaderInfo>

                <SectionWrapper>
                  <SectionCard>
                    <Text text={'Включить в отчёт'} type="captionB" />
                    <OptionsGroup>
                      <RadioButton
                        id="scope-all"
                        name="export-scope"
                        label={
                          <Text
                            text={'Все подсистемы'}
                            type={values.includeAllSubsystems ? 'bodyM' : 'bodyR'}
                          />
                        }
                        value={'all'}
                        checked={values.includeAllSubsystems}
                        onChange={() => setFieldValue('includeAllSubsystems', true)}
                      />
                      <RadioButton
                        id="scope-selected"
                        name="export-scope"
                        label={
                          <Text
                            text={'Выборочно'}
                            type={!values.includeAllSubsystems ? 'bodyM' : 'bodyR'}
                          />
                        }
                        value={'selected'}
                        checked={!values.includeAllSubsystems}
                        onChange={() => setFieldValue('includeAllSubsystems', false)}
                      />
                    </OptionsGroup>
                  </SectionCard>
                  {!values.includeAllSubsystems && (
                    <SecondarySectionCard>
                      <OptionsGroup>
                        {subsystems?.map((sub, index) => {
                          const isChecked = values.selectedSubsystemIds.includes(sub.id);
                          return (
                            <Checkbox
                              key={`export-subsystem-${sub.id}`}
                              id={`export-subsystem-${sub.id}`}
                              name={`export-subsystem-${sub.id}`}
                              checked={isChecked}
                              onChange={() => {
                                const next = isChecked
                                  ? values.selectedSubsystemIds.filter(
                                      id => id !== sub.id,
                                    )
                                  : [...values.selectedSubsystemIds, sub.id];
                                setFieldValue('selectedSubsystemIds', next);
                              }}
                              label={`${index + 1}. ${sub.name}`}
                              labelColor={theme.colors.basic00}
                            />
                          );
                        })}
                      </OptionsGroup>
                    </SecondarySectionCard>
                  )}
                </SectionWrapper>
                <SectionCard>
                  <div>
                    <Text text={'Детализация отчёта'} type="captionB" />
                    <Text
                      text={'Выберите уровень детализации отчёта.'}
                      type="tinyR"
                      color={theme.colors.basic02}
                    />
                  </div>
                  <OptionsGroup>
                    <RadioButton
                      id="detail-full"
                      name="export-details"
                      label={
                        <Text
                          text={'Данные по всем модулям и типам тестов'}
                          type={values.detailsLevel === 'full' ? 'bodyM' : 'bodyR'}
                        />
                      }
                      value={'full'}
                      checked={values.detailsLevel === 'full'}
                      onChange={() => setFieldValue('detailsLevel', 'full')}
                    />
                    <RadioButton
                      id="detail-summary-all"
                      name="export-details"
                      label={
                        <Text
                          text={'Данные общего покрытия подсистем и модулей'}
                          type={values.detailsLevel === 'summaryAll' ? 'bodyM' : 'bodyR'}
                        />
                      }
                      value={'summaryAll'}
                      checked={values.detailsLevel === 'summaryAll'}
                      onChange={() => setFieldValue('detailsLevel', 'summaryAll')}
                    />
                    <RadioButton
                      id="detail-summary-only"
                      name="export-details"
                      label={
                        <Text
                          text={'Только данные общего покрытия подсистем'}
                          type={values.detailsLevel === 'summaryOnly' ? 'bodyM' : 'bodyR'}
                        />
                      }
                      value={'summaryOnly'}
                      checked={values.detailsLevel === 'summaryOnly'}
                      onChange={() => setFieldValue('detailsLevel', 'summaryOnly')}
                    />
                  </OptionsGroup>
                </SectionCard>

                <SectionCard>
                  <OptionsGroup>
                    <RadioButton
                      id="export-pdf"
                      name="export-format"
                      label={
                        <Text
                          text={'Экспорт в PDF'}
                          type={values.format === 'pdf' ? 'bodyM' : 'bodyR'}
                        />
                      }
                      value={'pdf'}
                      checked={values.format === 'pdf'}
                      onChange={() => setFieldValue('format', 'pdf')}
                    />
                    <RadioButton
                      id="export-link"
                      name="export-format"
                      label={
                        <Text
                          text={'Сгенерировать ссылку'}
                          type={values.format === 'csv' ? 'bodyM' : 'bodyR'}
                        />
                      }
                      value={'csv'}
                      checked={values.format === 'csv'}
                      onChange={() => setFieldValue('format', 'csv')}
                    />
                  </OptionsGroup>
                </SectionCard>
                {values.format === 'csv' && (
                  <CopyLinkWrapper>
                    <Button
                      title={'Скопировать ссылку'}
                      variant="text"
                      fullWidth
                      iconPosition="left"
                      iconElement={<Link size={16} />}
                      onClick={() => handleSubmit(values as any)}
                      disabled={exportLoading}
                    />
                  </CopyLinkWrapper>
                )}
              </ModalContent>

              <ButtonsWrapper>
                <Button
                  title={'Экспортировать'}
                  variant="filled"
                  type="submit"
                  disabled={values.format === 'csv' || exportLoading}
                />
                <Button title={'Закрыть'} onClick={onCloseRequest} />
              </ButtonsWrapper>
            </form>
          )}
        </Formik>
      </ModalWrapper>
    </ModalWindow>
  );
};

export default ReportExportModal;
